import {
  Checkbox,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { ReactNode, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { TableColumn } from './TableColumn';
import { sharedColors, sharedStyles } from '../../utils/Style';
import { SortingOrder } from '../../interfaces/SortingOrder';
import { useLocalizedText } from '../../hooks/useLocalizedText';

type DataTableProps<T> = {
  columns: TableColumn<T>[];
  totalCount: number;
  rows: T[];
  additionalRows?: ReactNode[];
  limit?: number;
  setLimit?: (limit: number) => void;
  limitOptions?: number[];
  hidePagination?: boolean;
  page?: number;
  setPage?: (page: number) => void;
  orderBy?: string;
  order?: SortingOrder;
  onOrder?: (orderBy: string, order: SortingOrder) => void;
  onClick?: (row: T) => void;
  linkGenerator?: (row: T) => string;
  selectedRows?: T[];
  onSelectionChange?: (newSelectedRows: T[]) => void;
  headless?: boolean;
  loading?: boolean;
};

export function DataTable<T>({
  columns,
  totalCount,
  rows,
  additionalRows = [],
  limit = 0,
  setLimit = () => {},
  limitOptions = [5, 10, 15, 25, 50],
  hidePagination = false,
  page = 0,
  setPage = () => {},
  orderBy,
  order = 'asc',
  onOrder,
  onClick,
  linkGenerator,
  selectedRows = [],
  onSelectionChange,
  headless = false,
  loading = false,
}: DataTableProps<T>) {
  const t = useLocalizedText();

  const handleSort = useCallback(
    (columnID: string) => {
      if (columnID === orderBy) {
        onOrder?.(columnID, order === 'asc' ? 'desc' : 'asc');
      } else {
        onOrder?.(columnID, 'asc');
      }
    },
    [orderBy, order, onOrder],
  );

  const areAllRowsSelected = selectedRows.length === rows.length;

  const tableContent = (
    <TableContainer>
      <Table size='small'>
        <TableHead
          sx={{ backgroundColor: headless ? undefined : sharedColors.gray2 }}
        >
          <TableRow>
            {!!onSelectionChange && (
              <TableCell key='__selection__' align='center'>
                <Checkbox
                  size='small'
                  checked={areAllRowsSelected}
                  onChange={() =>
                    onSelectionChange(areAllRowsSelected ? [] : rows)
                  }
                />
              </TableCell>
            )}

            {columns.map((column) => {
              const content = (
                <Typography sx={sharedStyles.columnLabel}>
                  {column.name}
                </Typography>
              );
              return (
                <TableCell key={column.id} align={column.align}>
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleSort(column.id)}
                    >
                      {content}
                    </TableSortLabel>
                  ) : (
                    content
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const selected = selectedRows.includes(row);

            return (
              <TableRow
                key={i}
                onClick={onClick ? () => onClick(row) : undefined}
                sx={{
                  ...(onClick ? sharedStyles.editableRow : {}),
                  backgroundColor: selected
                    ? sharedColors.blue1
                    : sharedColors.white,
                }}
              >
                {!!onSelectionChange && (
                  <TableCell key='__selection__' align='center'>
                    <Checkbox
                      size='small'
                      checked={selected}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        onSelectionChange(
                          selected
                            ? selectedRows.filter((x) => x !== row)
                            : [...selectedRows, row],
                        );
                      }}
                    />
                  </TableCell>
                )}

                {columns.map((column) => (
                  <TableCell key={column.id} align={column.align}>
                    {linkGenerator ? (
                      <Link
                        to={linkGenerator(row)}
                        style={{ textDecoration: 'none' }}
                      >
                        {column.accessor(row)}
                      </Link>
                    ) : (
                      column.accessor(row)
                    )}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}

          {loading && (
            <TableRow>
              <TableCell align='center' colSpan={columns.length}>
                <CircularProgress sx={{ m: 2.5 }} />
              </TableCell>
            </TableRow>
          )}

          {additionalRows}
        </TableBody>
      </Table>
      {!hidePagination && (
        <TablePagination
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={limit}
          onRowsPerPageChange={(e) => setLimit(Number(e.target.value))}
          rowsPerPageOptions={limitOptions}
          component='div'
          labelRowsPerPage={t('data_table.rows_per_page')}
          labelDisplayedRows={(p) => t('data_table.pagination', p)}
        />
      )}
    </TableContainer>
  );

  return headless ? (
    tableContent
  ) : (
    <Paper sx={{ ...sharedStyles.tableContainer }}>{tableContent}</Paper>
  );
}
